import type { PersistedGraph, PersistedEdge } from '../../models/KnowledgeGraph';
import type { BubbleNode } from './focusFilter';

/**
 * Cross-bubble topology aggregator for the Compass Explorer L1/L2
 * view. Answers "which bubbles talk to which", rolled up so callers
 * can draw a thin line between each connected pair and surface
 * boundary chips on the expanded bubble when the peer is still
 * collapsed.
 *
 * Conceptually:
 *   - walk the containment edges once to map every resource to its
 *     owning bubble (the VPC or account shown as a card)
 *   - iterate non-containment edges; any edge whose endpoints land in
 *     two different bubbles becomes a cross-bubble signal
 *   - aggregate by unordered pair so A→B and B→A fold into one line
 *   - also record per-bubble boundary chips when exactly one endpoint
 *     is inside an expanded bubble
 *
 * Pure function. No store reads, no scene mutation.
 */

export interface BubbleTopologyEdge {
  /** Stable id for the scene — sorted pair keeps diffs minimal. */
  id: string;
  fromBubbleId: string;
  toBubbleId: string;
  /** How many raw graph edges folded into this pair. */
  count: number;
  /** Distinct edge types that contributed (e.g. `peering`, `allows`). */
  kinds: string[];
  /**
   * True when both endpoints were expanded when the topology was
   * computed. Callers can render these in fuller weight so users know
   * the connection is fully "open."
   */
  bothExpanded: boolean;
}

export interface BubbleBoundaryChip {
  /** Bubble id the chip belongs to (the expanded one). */
  bubbleId: string;
  /** Peer bubble the chip points at (still collapsed). */
  targetBubbleId: string;
  /** Peer bubble's human name — for the chip label. */
  targetLabel: string;
  /** 'out' when traffic leaves this bubble, 'in' when it arrives. */
  direction: 'in' | 'out';
  count: number;
  kinds: string[];
}

export interface BubbleTopology {
  edges: BubbleTopologyEdge[];
  /** `bubbleId → chip[]` so the renderer can attach them to attrs. */
  chipsByBubble: Map<string, BubbleBoundaryChip[]>;
}

export function computeBubbleTopology(
  graph: PersistedGraph,
  bubbles: readonly BubbleNode[],
  expandedRefSet: ReadonlySet<string>,
): BubbleTopology {
  const bubbleByRef = new Map<string, BubbleNode>();
  for (const b of bubbles) {
    bubbleByRef.set(b.referenceId, b);
    for (const memberId of b.memberIds ?? []) {
      bubbleByRef.set(memberId, b);
    }
  }

  const parentOf = new Map<string, string>();
  for (const e of graph.edges) {
    if (e.type !== 'contains' && e.type !== 'containment') continue;
    if (!parentOf.has(e.to)) parentOf.set(e.to, e.from);
  }

  // Map each resource up its containment chain to the bubble it belongs
  // to. `undefined` means the resource sits outside the bubble grid
  // (orphaned top-level node, or a container whose own bubble isn't
  // visible at this zoom) — we ignore those.
  const resourceToBubble = new Map<string, BubbleNode>();
  const resolve = (id: string): BubbleNode | undefined => {
    const cached = resourceToBubble.get(id);
    if (cached) return cached;
    const seen = new Set<string>();
    let cursor: string | undefined = id;
    while (cursor && !seen.has(cursor)) {
      const bubble = bubbleByRef.get(cursor);
      if (bubble) {
        resourceToBubble.set(id, bubble);
        return bubble;
      }
      seen.add(cursor);
      cursor = parentOf.get(cursor);
    }
    return undefined;
  };

  // Aggregate by unordered pair so A→B and B→A collapse together.
  interface PairAcc {
    a: BubbleNode;
    b: BubbleNode;
    count: number;
    kinds: Set<string>;
  }
  const pairs = new Map<string, PairAcc>();
  const pairKey = (x: BubbleNode, y: BubbleNode) => (
    x.id < y.id ? `${x.id}|${y.id}` : `${y.id}|${x.id}`
  );

  // Per-bubble chip accumulators. Keyed by source bubble + target
  // bubble + direction so we can show "→ prod" and "← prod" chips
  // separately when both directions occur.
  interface ChipAcc {
    bubble: BubbleNode;
    targetBubble: BubbleNode;
    direction: 'in' | 'out';
    count: number;
    kinds: Set<string>;
  }
  const chips = new Map<string, ChipAcc>();

  for (const e of graph.edges) {
    if (e.type === 'contains' || e.type === 'containment') continue;
    const src = resolve(e.from);
    const tgt = resolve(e.to);
    if (!src || !tgt) continue;
    if (src.id === tgt.id) continue; // intra-bubble; belongs to the dot grid

    // Pair aggregation
    const key = pairKey(src, tgt);
    let acc = pairs.get(key);
    if (!acc) {
      acc = {
        a: src.id < tgt.id ? src : tgt,
        b: src.id < tgt.id ? tgt : src,
        count: 0,
        kinds: new Set(),
      };
      pairs.set(key, acc);
    }
    acc.count++;
    acc.kinds.add(edgeKind(e));

    // Boundary chips — only record when exactly one endpoint is expanded.
    const srcExpanded = isBubbleExpanded(src, expandedRefSet);
    const tgtExpanded = isBubbleExpanded(tgt, expandedRefSet);
    if (srcExpanded && !tgtExpanded) {
      upsertChip(chips, src, tgt, 'out', e);
    }
    if (tgtExpanded && !srcExpanded) {
      upsertChip(chips, tgt, src, 'in', e);
    }
  }

  const edges: BubbleTopologyEdge[] = Array.from(pairs.values()).map((p) => ({
    id: `bubble-edge:${p.a.id}:${p.b.id}`,
    fromBubbleId: p.a.id,
    toBubbleId: p.b.id,
    count: p.count,
    kinds: Array.from(p.kinds).sort(),
    bothExpanded: isBubbleExpanded(p.a, expandedRefSet) && isBubbleExpanded(p.b, expandedRefSet),
  }));

  const chipsByBubble = new Map<string, BubbleBoundaryChip[]>();
  for (const chip of chips.values()) {
    const bubbleId = chip.bubble.id;
    const peer = chip.targetBubble;
    const list = chipsByBubble.get(bubbleId) ?? [];
    list.push({
      bubbleId,
      targetBubbleId: peer.id,
      targetLabel: peer.label,
      direction: chip.direction,
      count: chip.count,
      kinds: Array.from(chip.kinds).sort(),
    });
    chipsByBubble.set(bubbleId, list);
  }
  // Sort chips by count so the busiest peers render first.
  for (const list of chipsByBubble.values()) {
    list.sort((a, b) => b.count - a.count);
  }

  return { edges, chipsByBubble };
}

function edgeKind(e: PersistedEdge): string {
  return String(e.type ?? 'flow');
}

function upsertChip(
  chips: Map<string, { bubble: BubbleNode; targetBubble: BubbleNode; direction: 'in' | 'out'; count: number; kinds: Set<string> }>,
  bubble: BubbleNode,
  targetBubble: BubbleNode,
  direction: 'in' | 'out',
  edge: PersistedEdge,
): void {
  const key = `${bubble.id}|${targetBubble.id}|${direction}`;
  let chip = chips.get(key);
  if (!chip) {
    chip = {
      bubble,
      targetBubble,
      direction,
      count: 0,
      kinds: new Set(),
    };
    chips.set(key, chip);
  }
  chip.count++;
  chip.kinds.add(edgeKind(edge));
}

function isBubbleExpanded(bubble: BubbleNode, expandedRefSet: ReadonlySet<string>): boolean {
  return expandedRefSet.has(bubble.referenceId) || expandedRefSet.has(bubble.id);
}
